import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { pct, profitFactorText } from '../utils/dynamanSimulation';
import { calculatePortfolioMetrics } from '../utils/portfolioMetrics';

/**
 * The aggregate dashboard: seven numbers, above the ledger.
 *
 * <h2>Every tile says what it excludes</h2>
 *
 * Each figure covers REALISED trades only, and the panel says so once, prominently, rather than in
 * a footnote. On this rule set an open position can be worth more than every closed trade put
 * together — the SPY run's unrealised mark is over 1,500% — so a reader who assumes the headline
 * includes it is not slightly wrong, they are reading a different number entirely.
 *
 * <h2>IT COMPUTES NOTHING. Every figure comes from calculatePortfolioMetrics</h2>
 *
 * It used to render the backend's own {@code statistics} block while the Running Ledger computed
 * its own, summing percentages where this one compounded them — so the two panels reported the
 * same trades and different Net PnL, Equity and Max Drawdown. Both now fold the SAME trade list
 * through the SAME reducer, which is the only arrangement in which they cannot drift: not two
 * implementations kept in agreement by care, but one implementation read twice.
 *
 * <p>The backend's block is still computed and still authoritative on the server; a test pins
 * this utility's output against it to the decimal, so a divergence fails a build rather than
 * reaching a reader as two numbers that disagree.
 *
 * <h2>Colour encodes direction, never magnitude</h2>
 *
 * Green for profitable, red for not, grey for the ones with no natural direction (trade count,
 * holding period). Shading by size would make a 3% win and a 300% win look like different kinds of
 * thing, and on a series with one enormous outlier the whole panel would read as uniformly green.
 */

/** Currency, grouped, with a sign — so a loss is unmistakable at a glance. */
const money = (v) => (Number.isFinite(v)
  ? `${v >= 0 ? '+' : '-'}${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—');

/**
 * The sized account's return. Derived from the two figures already on the metrics object rather
 * than carried as a third, so it cannot disagree with the Equity tile beside it.
 */
const netReturnOf = (s) => (Number.isFinite(s.endingEquity) && s.endingEquity - s.netMoney > 0
  ? (s.netMoney / (s.endingEquity - s.netMoney)) * 100
  : null);

/** An absent profit factor means no losing trades — an undefined ratio, not a good one. */
const toneFor = (key, value) => {
  if (value === null || value === undefined) return 'text.secondary';
  switch (key) {
    case 'totalNetPnlPercent':
    case 'averageWinPercent':
      return value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary';
    case 'averageLossPercent':
      return value < 0 ? 'error.main' : 'text.primary';
    case 'profitFactor':
      return value >= 1 ? 'success.main' : 'error.main';
    case 'maxDrawdownPercent':
      return value > 0 ? 'error.main' : 'text.primary';
    case 'winRatePercent':
      return value >= 50 ? 'success.main' : 'text.primary';
    default:
      return 'text.primary';
  }
};

const Tile = ({ label, value, hint, tone }) => (
  <Tooltip title={hint || ''} arrow placement="top">
    <Card variant="outlined" sx={{ minWidth: 150, flex: '1 1 150px' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: tone, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  </Tooltip>
);

Tile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  tone: PropTypes.string,
};

const DynaManStatsPanel = ({ trades, initialCapital, rejectionCount }) => {
  // ORDER IS LOAD-BEARING: max drawdown is path-dependent, so the list must stay in the order
  // the trades were taken. It arrives that way and is not re-sorted here.
  const s = React.useMemo(
    () => calculatePortfolioMetrics(trades, initialCapital),
    [trades, initialCapital],
  );
  if (!trades || trades.length === 0) return null;
  const tiles = [
    {
      key: 'totalNetPnlPercent',
      label: 'Total Net PnL',
      value: pct(s.totalNetPnlPercent),
      hint: 'Compounded across trades in the order they were taken, 100% of capital per trade, '
        + 'no costs. Not a sum of percentages — adding returns on different bases is not a return.',
    },
    {
      key: 'winRatePercent',
      label: 'Win Rate',
      value: `${s.winRatePercent.toFixed(1)}%`,
      hint: `${s.wins} won, ${s.losses} lost. A scratch trade counts as a loss, so this cannot be `
        + 'inflated by flat trades.',
    },
    {
      key: 'profitFactor',
      label: 'Profit Factor',
      value: profitFactorText(s.profitFactor),
      hint: 'Gross gains divided by gross losses. Shown as n/a when there were no losing trades — '
        + 'that is an absent denominator, not a good score.',
    },
    {
      key: 'averageWinPercent',
      label: 'Average Win',
      value: pct(s.averageWinPercent),
      hint: 'Mean return of the winning trades.',
    },
    {
      key: 'averageLossPercent',
      label: 'Average Loss',
      value: pct(s.averageLossPercent),
      hint: 'Mean return of the losing trades, signed negative so it reads against the average win.',
    },
    {
      key: 'maxDrawdownPercent',
      label: 'Max Drawdown',
      value: `${s.maxDrawdownPercent.toFixed(2)}%`,
      hint: 'Deepest peak-to-trough fall of the TRADE-SEQUENCE equity curve. A bar-by-bar curve '
        + 'would show a deeper figure; this is what a list of closed trades can honestly support.',
    },
    {
      key: 'totalGrossPoints',
      label: 'Gross Points',
      value: `${s.totalGrossPoints >= 0 ? '+' : ''}${s.totalGrossPoints.toFixed(2)}`,
      hint: 'SUMMED price points, pre-cost — what a fixed-contract book made. Summed and not '
        + 'compounded because points are currency, not a rate; but a sum across decades of a '
        + 'large price range weights the recent years enormously, so read it beside Total Net '
        + 'PnL rather than instead of it.',
    },
    {
      key: 'averageRewardToRisk',
      label: 'Reward : Risk',
      value: s.averageRewardToRisk === null ? 'n/a'
        : `${s.averageRewardToRisk.toFixed(2)} : 1`,
      hint: 'Mean R of the WINNERS ALONE — the asymmetry a "4:1 book" claims. NOT the same as '
        + `mean R (${s.averageRMultiple.toFixed(2)}), which is expectancy over every trade and is `
        + 'a much smaller number: a 4:1 book at a 25% win rate has an expectancy near zero R. '
        + (s.payoffRatio === null ? 'n/a when nothing lost.'
          : `Payoff ratio ${s.payoffRatio.toFixed(2)}:1, so break-even needs a `
            + `${(100 / (1 + s.payoffRatio)).toFixed(1)}% win rate against the actual `
            + `${s.winRatePercent.toFixed(1)}%.`),
    },
    {
      key: 'tradeCount',
      label: 'Trades',
      value: s.tradeCount,
      hint: `${s.tradeCount} realised. Mean hold ${s.averageTradingDays.toFixed(1)} trading days, `
        + `mean ${s.averageRMultiple.toFixed(2)}R.`,
    },
  ];

  /*
   * THE SIZED HALF, and the reason it is here rather than in a second panel.
   *
   * Run Full Simulation sent no account, so it could only ever show the GROSS compounded
   * figure; the replay has always sent one, so its HUD shows the sized NET return. Both are
   * correct and they are different quantities -- +124.05% against +86.50% on the same 162
   * trades -- which read as the two surfaces disagreeing about one run. Showing both here, with
   * the same labels the Running Ledger uses, is what makes them comparable.
   *
   * Appended rather than interleaved: with no account these tiles are absent and the panel is
   * exactly what it was.
   */
  if (s.sized) {
    tiles.push(
      {
        key: 'endingEquity',
        label: 'Equity',
        value: money(s.endingEquity),
        hint: 'Starting equity plus the summed net PnL. Summed and NOT compounded again, because '
          + 'each trade was already sized against the equity standing when it was taken — '
          + 'compounding them a second time would count it twice.',
      },
      {
        key: 'netMoney',
        label: 'Net P&L',
        value: money(s.netMoney),
        hint: 'After commission and slippage. The gap between this and Total Net PnL is friction '
          + 'plus the difference between a risk-sized book and staking 100% every trade.',
      },
      {
        key: 'netReturnPercent',
        label: 'Return (net)',
        value: pct(netReturnOf(s)),
        hint: 'The sized account’s return — what the Running Ledger reports. Lower than Total '
          + 'Net PnL because that figure is unsized and pre-cost, not because the two disagree.',
      },
      {
        key: 'maxDrawdownMoneyPercent',
        label: 'Max DD (currency)',
        value: `${s.maxDrawdownMoneyPercent.toFixed(2)}%`,
        hint: 'Deepest fall of the CURRENCY equity curve. Usually shallower than the trade-'
          + 'sequence figure, because risk sizing stakes less than the whole account per trade.',
      },
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {tiles.map((t) => (
          <Tile
            key={t.key}
            label={t.label}
            value={t.value}
            hint={t.hint}
            tone={toneFor(t.key, s[t.key])}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Realised trades only — any open position is excluded from every figure above.
        {s.sized
          ? ' Total Net PnL is UNSIZED and pre-cost (100% of capital per trade); Return (net) is '
            + 'the risk-sized account after commission and slippage. Both describe the same '
            + 'trades — the Running Ledger shows the same pair.'
          : ' No commission, no slippage beyond the gap rule, no position sizing.'}
        {rejectionCount ? ` ${rejectionCount} structural triggers were declined; see the Signal Audit tab.` : ''}
      </Typography>
    </Box>
  );
};

DynaManStatsPanel.propTypes = {
  /**
   * The CLOSED trades, in the order they were taken. Not a precomputed statistics block: this
   * panel and the Running Ledger must fold one list through one reducer, or they can disagree.
   */
  trades: PropTypes.arrayOf(PropTypes.shape({
    pnlPercent: PropTypes.number,
    netPnl: PropTypes.number,
    rMultiple: PropTypes.number,
    tradingDays: PropTypes.number,
  })),
  /** The account's starting equity, or null for an unsized run. */
  initialCapital: PropTypes.number,
  rejectionCount: PropTypes.number,
};

DynaManStatsPanel.defaultProps = {
  trades: [],
  initialCapital: null,
  rejectionCount: undefined,
};

export default DynaManStatsPanel;
